import { push } from 'connected-react-router';
import { all, call, fork, put, select, spawn, take } from 'redux-saga/effects';
import * as form from '../../containers/FormMessage/actions';
import * as notify from '../../containers/layout/Notifications/actions';
import { HOME, LANDING, LOGIN_CONSENTS } from '../../routes/paths';
import { endOfWeek } from '../../utils/dates/date';
import { serviceUnavailable } from '../app/actions';
import {
  ConnectionError,
  NotFoundError,
  ServiceUnavailableError,
  UnauthorizedAccessError
} from '../app/errors';
import { getBets } from '../bets/rest';
import { close, open } from '../modal/actions';
import { SlipConflictError, SlipUnsupportedShopError } from '../slip/errors';
import { clearSlips, setSlips } from '../slip/storage';
import * as user from '../user/actions';
import { isAuthenticated } from '../user/selectors';
import {
  clearMobileNumber,
  LOGOUT,
  LOGOUT_CONFIRM,
  LOGOUT_REJECT,
  mobileNumberValid,
  RESEND_VERIFICATION_CODE,
  RESTART_LOGIN_PROCESS,
  SUBMIT_BET_SLIP_NUMBER,
  SUBMIT_MOBILE_NUMBER,
  SUBMIT_VERIFICATION_CODE
} from './actions';
import BadPhoneNumberError, { VerificationConflictError } from './errors';
import { postInvalidateToken, postMobileNumber, postVerificationCode } from './rest';
import { clearStoredMobileNumber } from './storage';

/* eslint-disable no-constant-condition */
export function* trackAsAnonymousUser() {
  while (true) {
    const action = yield take(SUBMIT_BET_SLIP_NUMBER);
    const { betSlipNumber } = action;

    try {
      yield put(form.clearMessage());
      yield put(open({ componentName: 'RequestInProgress' }));
      yield call(getBets, [betSlipNumber]);
      yield put(close());
      yield call(setSlips, [betSlipNumber], endOfWeek(new Date()).getTime());
      yield put(user.setAnonymous());
      yield put(push(HOME));
      yield call(clearStoredMobileNumber);
    } catch (err) {
      yield put(close());

      if (err instanceof NotFoundError) {
        yield put(form.setMessage(
          form.ERROR,
          'There was a problem adding your bet. Please check the highlighted field.',
          { betSlipNumber: 'You have entered an invalid slip number' }
        ));
      } else if (err instanceof UnauthorizedAccessError) {
        yield put(form.setMessage(
          form.ERROR,
          'There was a problem adding your bet. Please check the highlighted field.',
          { betSlipNumber: 'You cannot track this slip!' }
        ));
      } else if (err instanceof ConnectionError) {
        yield put(notify.add('Connection error.', notify.ERROR));
      } else if (err instanceof ServiceUnavailableError) {
        yield put(serviceUnavailable());
      } else if (err instanceof SlipConflictError) {
        yield put(form.setMessage(
          form.ERROR,
          'You cannot track this slip as it is already being tracked by another account',
          { betSlipNumber: ' ' }
        ));
      } else if (err instanceof SlipUnsupportedShopError) {
        yield put(form.setMessage(
          form.ERROR,
          'Sorry, Bet Tracker is currently in limited trial.'
          + ' Check with your local shop for availability.',
          { betSlipNumber: ' ' }
        ));
      } else {
        throw err;
      }
    }
  }
}

export function* mobileNumberSubmit(mobileNumber) {
  try {
    yield put(form.clearMessage());
    yield put(open({ componentName: 'RequestInProgress' }));
    yield call(postMobileNumber, mobileNumber);
    yield put(close());
    yield put(mobileNumberValid(mobileNumber));
  } catch (err) {
    yield put(close());

    if (err instanceof BadPhoneNumberError) {
      yield put(clearMobileNumber());
      yield put(form.setMessage(
        form.ERROR,
        'Card Cancelled. Your PLUS card is no longer active. Please call William Hill Customer Operations on 0330 123 9638 for more information.',
        { mobileNumber: 'Wrong mobile number!' }
      ));
    } else if (err instanceof ConnectionError) {
      yield put(notify.add('Connection error.', notify.ERROR));
    } else if (err instanceof ServiceUnavailableError) {
      yield put(serviceUnavailable());
    } else {
      throw err;
    }
  }
}

export function* watchMobileNumberSubmit() {
  while (true) {
    const action = yield take(SUBMIT_MOBILE_NUMBER);
    const { mobileNumber } = action;
    yield fork(mobileNumberSubmit, mobileNumber);
  }
}

export function* resendVerificationCode(mobileNumber) {
  try {
    yield put(form.clearMessage());
    yield put(open({ componentName: 'RequestInProgress' }));
    yield call(postMobileNumber, mobileNumber);
    yield put(close());
    yield put(form.setMessage(
      form.SUCCESS,
      'Verification code has been sent again.'
    ));
  } catch (err) {
    yield put(close());
    if (err instanceof BadPhoneNumberError) {
      yield put(clearMobileNumber());
      yield put(form.setMessage(
        form.ERROR,
        'Card Cancelled. Your PLUS card is no longer active. Please call William Hill Customer Operations on 0330 123 9638 for more information.',
        { verificationCode: 'Wrong mobile number!' }
      ));
    } else if (err instanceof ConnectionError) {
      yield put(notify.add('Connection error.', notify.ERROR));
    } else if (err instanceof ServiceUnavailableError) {
      yield put(serviceUnavailable());
    } else {
      throw err;
    }
  }
}

export function* watchResendVerificationCode() {
  while (true) {
    const action = yield take(RESEND_VERIFICATION_CODE);
    const { mobileNumber } = action;
    yield call(resendVerificationCode, mobileNumber);
  }
}

export function* verificationCodeSubmit(mobileNumber, verificationCode) {
  try {
    yield put(form.clearMessage());
    yield put(open({ componentName: 'RequestInProgress' }));
    yield call(postVerificationCode, mobileNumber, verificationCode);
    yield put(close());
    yield put(clearMobileNumber());
    yield put(user.logIn());
    yield put(push(LOGIN_CONSENTS));
    yield call(clearStoredMobileNumber);
  } catch (err) {
    yield put(close());
    if (err instanceof UnauthorizedAccessError) {
      yield put(form.setMessage(
        form.ERROR,
        'Incorrect verification code. Please check and try again.',
        { verificationCode: ' ' }
      ));
    } else if (err instanceof VerificationConflictError) {
      yield put(open({ componentName: 'PhoneAlreadyRegistered' }));
    } else if (err instanceof ConnectionError) {
      yield put(notify.add('Connection error.', notify.ERROR));
    } else if (err instanceof ServiceUnavailableError) {
      yield put(serviceUnavailable());
    } else {
      throw err;
    }
  }
}

export function* watchVerificationCodeSubmit() {
  while (true) {
    const action = yield take(SUBMIT_VERIFICATION_CODE);
    const { mobileNumber, verificationCode } = action;
    yield fork(verificationCodeSubmit, mobileNumber, verificationCode);
  }
}

export function* watchRestartLoginProcess() {
  while (yield take(RESTART_LOGIN_PROCESS)) {
    yield put(form.clearMessage());
    yield call(clearStoredMobileNumber);
  }
}

export function* restartUserJourney() {
  yield put(user.logOut());
  yield put(push(LANDING));
  yield put(notify.add('You have been logged out!', notify.ERROR));
}

function* logoutAuthenticatedUser() {
  try {
    yield put(open({ componentName: 'RequestInProgress' }));
    yield call(postInvalidateToken);
    yield put(close());
    yield put(user.logOut());
    yield put(push(LANDING));
    yield put(notify.add('Logged out!', notify.INFO));
  } catch (err) {
    yield put(close());
    if (err.status === 403) {
      yield put(user.logOut());
      yield put(push(LANDING));
      yield put(notify.add('Logged out!', notify.INFO));
    } else {
      yield put(notify.add('Connection error.', notify.ERROR));
    }
  }
}

function* logoutAnonymousUser() {
  yield put(open({ componentName: 'LogoutAnonymousUserConfirm' }));
  const { type } = yield take([LOGOUT_CONFIRM, LOGOUT_REJECT]);

  if (type === LOGOUT_CONFIRM) {
    yield call(clearSlips);
    yield put(user.logOut());
    yield put(push(LANDING));
    yield put(notify.add('Logged out!', notify.INFO));
  }

  yield put(close());
}

export function* handleLogoutRequest() {
  while (yield take(LOGOUT)) {
    const authenticated = yield select(isAuthenticated);

    if (authenticated) {
      yield call(logoutAuthenticatedUser);
    } else {
      yield call(logoutAnonymousUser);
    }
  }
}

export default function* root() {
  yield all([
    spawn(trackAsAnonymousUser),
    spawn(watchMobileNumberSubmit),
    spawn(watchResendVerificationCode),
    spawn(watchRestartLoginProcess),
    spawn(watchVerificationCodeSubmit),
    spawn(handleLogoutRequest)
  ]);
}
/* eslint-enable no-constant-condition */
